const I18N_ERROR_KEY = /^global\.error\.[a-zA-Z]+$/
const NOT_FOUND_MESSAGE = 'global.error.pageNotFound'
const SERVER_ERROR_MESSAGE = 'global.error.serverError'

interface ISanitizableError {
  statusCode?: number
  statusMessage?: string
  message?: string
  url?: string
  stack?: string
  data?: unknown
  description?: string
}

interface ISanitizablePayload {
  error?: unknown
  _errors?: Record<string, unknown>
}

export const getSafeStatusMessage = (statusCode?: number, statusMessage?: string) => {
  if (statusMessage && I18N_ERROR_KEY.test(statusMessage)) return statusMessage

  return statusCode === 404 ? NOT_FOUND_MESSAGE : SERVER_ERROR_MESSAGE
}

const succeeds = (write: () => void) => {
  try {
    write()

    return true
  } catch {
    return false
  }
}

const overwriteField = (target: ISanitizableError, key: keyof ISanitizableError, value: unknown) => {
  const writable = target as Record<string, unknown>

  const assigned = succeeds(() => {
    writable[key] = value
    if (writable[key] !== value) throw new Error('unwritable')
  })

  if (assigned) return

  succeeds(() => Object.defineProperty(target, key, { value, writable: true, configurable: true, enumerable: true }))
}

const sanitizeError = (error: unknown) => {
  if (!error || typeof error !== 'object') return

  const sanitizable = error as ISanitizableError
  const safeStatusMessage = getSafeStatusMessage(sanitizable.statusCode, sanitizable.statusMessage)

  overwriteField(sanitizable, 'statusMessage', safeStatusMessage)
  overwriteField(sanitizable, 'message', safeStatusMessage)
  overwriteField(sanitizable, 'data', undefined)

  if ('url' in sanitizable) overwriteField(sanitizable, 'url', '')
  if ('stack' in sanitizable) overwriteField(sanitizable, 'stack', '')
  if ('description' in sanitizable) overwriteField(sanitizable, 'description', '')
}

export const sanitizeErrorPayload = (payload: ISanitizablePayload, { includeRenderedError = true } = {}) => {
  if (includeRenderedError) {
    sanitizeError(payload.error)
  }

  for (const error of Object.values(payload._errors ?? {})) {
    sanitizeError(error)
  }
}
